import { useEffect, useState } from "react";
import { ArrowRight, Bot, CircleCheck, Link2 } from "lucide-react";

type RunEvent = {
  at: string;
  runId: string;
  provider: string;
  model: string;
};

type ZeroToCitedResponse = {
  brand: string;
  experiment: {
    status: "monitoring" | "partial" | "proven";
    publication: {
      id: string;
      url: string;
      title: string | null;
      publishedAt: string;
    };
    events: {
      published: { at: string };
      pageCrawlerVisit: {
        at: string;
        family: string | null;
        source: string | null;
        scope: "url";
      } | null;
      domainCrawlerActivity: {
        at: string;
        windowStart: string;
        windowEnd: string;
        requests: number;
        families: Array<{ family: string; requests: number }>;
        scope: "domain_daily";
      } | null;
      firstMention: RunEvent | null;
      firstCitation: (RunEvent & { url: string }) | null;
    };
    limitations: string[];
  };
};

type BenchmarkResponse = {
  slug: string;
  metric: "mention_rate";
  totals: { successfulAnswers: number; mentionedAnswers: number };
  brands: Array<{
    label: string;
    successfulAnswers: number;
    mentionedAnswers: number;
    mentionRate: number;
    prompts: Array<{
      text: string;
      successfulAnswers: number;
      mentionedAnswers: number;
      mentionRate: number;
    }>;
  }>;
  prompts: string[];
  surfaces: Array<{ provider: string; model: string }>;
  updatedAt: string | null;
};

const cloudUrl = "https://cloud.aeokit.dev";
const agentUrl = "https://www.npmjs.com/package/aeo-agent";
const evidenceEndpoint = `${cloudUrl}/api/public/zero-to-cited/macspotter-zero-to-cited`;
const benchmarkEndpoint = `${cloudUrl}/api/public/benchmarks/crm-visibility`;

function displayTime(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
}

function elapsed(start: string, end: string): string {
  const milliseconds = new Date(end).getTime() - new Date(start).getTime();
  if (milliseconds < 0 || !Number.isFinite(milliseconds)) return "Observed";
  const minutes = Math.round(milliseconds / 60_000);
  if (minutes < 60) return `+${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours < 24) return `+${hours}h${remainder ? ` ${remainder}m` : ""}`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `+${days}d${remainingHours ? ` ${remainingHours}h` : ""}`;
}

function EvidenceStory({ data }: { data: ZeroToCitedResponse }) {
  const { brand, experiment } = data;
  const { publication, events } = experiment;
  const timeline = [
    {
      label: "Published",
      detail: displayTime(events.published.at),
      verified: true,
      Icon: CircleCheck,
    },
    {
      label: events.pageCrawlerVisit
        ? "URL crawler visit"
        : "URL crawl not yet verified",
      detail: events.pageCrawlerVisit
        ? `${events.pageCrawlerVisit.family ?? "AI crawler"} · ${elapsed(publication.publishedAt, events.pageCrawlerVisit.at)}`
        : "Domain totals cannot prove that this page was visited.",
      verified: Boolean(events.pageCrawlerVisit),
      Icon: Bot,
    },
    {
      label: "First mention",
      detail: events.firstMention
        ? `${events.firstMention.provider} · ${elapsed(publication.publishedAt, events.firstMention.at)}`
        : "Waiting for a tracked answer",
      verified: Boolean(events.firstMention),
      Icon: CircleCheck,
    },
    {
      label: "First citation",
      detail: events.firstCitation
        ? `${events.firstCitation.provider} · ${elapsed(publication.publishedAt, events.firstCitation.at)}`
        : "Waiting for an exact-URL citation",
      verified: Boolean(events.firstCitation),
      Icon: Link2,
    },
  ];

  return (
    <section className="evidence-card" aria-labelledby="zero-to-cited-title">
      <div className="evidence-heading">
        <div>
          <p className="eyebrow">Live aeokit evidence</p>
          <h2 id="zero-to-cited-title">{brand}: zero to cited</h2>
          <a href={publication.url}>{publication.title ?? publication.url}</a>
        </div>
        <span className={`evidence-status ${experiment.status}`}>
          {experiment.status === "proven"
            ? "Proven"
            : experiment.status === "partial"
              ? "Partially observed"
              : "Monitoring"}
        </span>
      </div>
      <div className="evidence-timeline">
        {timeline.map(({ label, detail, verified, Icon }) => (
          <article className={verified ? "verified" : "pending"} key={label}>
            <Icon aria-hidden="true" />
            <h3>{label}</h3>
            <p>{detail}</p>
          </article>
        ))}
      </div>
      {events.domainCrawlerActivity && !events.pageCrawlerVisit ? (
        <p className="evidence-limitation">
          Aeokit observed{" "}
          {events.domainCrawlerActivity.requests.toLocaleString()} automated
          requests in the first overlapping daily window. This is a domain-level
          signal, not proof that this URL was crawled.
        </p>
      ) : null}
    </section>
  );
}

export function SeoPage() {
  const [story, setStory] = useState<ZeroToCitedResponse | null>(null);
  const [benchmark, setBenchmark] = useState<BenchmarkResponse | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(evidenceEndpoint, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Evidence API ${response.status}`);
        return (await response.json()) as ZeroToCitedResponse;
      })
      .then(setStory)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setStory(null);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(benchmarkEndpoint, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Benchmark API ${response.status}`);
        return (await response.json()) as BenchmarkResponse;
      })
      .then(setBenchmark)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setBenchmark(null);
      });
    return () => controller.abort();
  }, []);

  return (
    <>
      <section className="seo-hero">
        <div className="container narrow">
          <p className="eyebrow">AEO vs SEO</p>
          <h1>
            A new source can enter an AI answer before it earns a search
            ranking.
          </h1>
          <p className="hero-copy">
            SEO competes to rank a page. AEO makes original, verifiable evidence
            available to live answer engines. Aeokit records what actually
            happens from publication to citation.
          </p>
        </div>
      </section>
      <section className="seo-evidence section">
        <div className="container">
          {story ? (
            <EvidenceStory data={story} />
          ) : (
            <section className="evidence-card evidence-empty">
              <p className="eyebrow">Live aeokit evidence</p>
              <h2>The zero to cited experiment is being prepared.</h2>
              <p>
                Aeokit will publish each milestone only when production evidence
                supports it. No estimated crawl or citation times.
              </p>
            </section>
          )}
        </div>
      </section>
      <section
        className="benchmark-section section"
        aria-labelledby="crm-benchmark-title"
      >
        <div className="container">
          <div className="benchmark-heading">
            <div>
              <p className="eyebrow">Live production benchmark</p>
              <h2 id="crm-benchmark-title">CRM answer visibility</h2>
              <p>
                Share of successful tracked answers that mention each monitored
                brand across common CRM buying questions.
              </p>
            </div>
            <span>
              {benchmark?.updatedAt
                ? `Updated ${displayTime(benchmark.updatedAt)}`
                : "Loading live data…"}
            </span>
          </div>
          {benchmark ? (
            <div className="benchmark-card">
              <div className="benchmark-bars">
                {benchmark.brands.map((brand) => (
                  <article key={brand.label}>
                    <div className="benchmark-label">
                      <strong>{brand.label}</strong>
                      <span>{brand.mentionRate}%</span>
                    </div>
                    <div className="benchmark-track" aria-hidden="true">
                      <span style={{ width: `${brand.mentionRate}%` }} />
                    </div>
                    <p>
                      {brand.mentionedAnswers} of {brand.successfulAnswers}{" "}
                      answers
                    </p>
                  </article>
                ))}
              </div>
              <p className="benchmark-method">
                Mention visibility only—not citation visibility. Based on{" "}
                {benchmark.totals.successfulAnswers} successful tracked answers;
                failed runs are excluded.
              </p>
              <details className="benchmark-breakdown">
                <summary>See results by prompt</summary>
                <div>
                  {benchmark.brands.map((brand) => (
                    <section key={brand.label}>
                      <h4>{brand.label}</h4>
                      <ul>
                        {brand.prompts.map((prompt) => (
                          <li key={prompt.text}>
                            <span>{prompt.text}</span>
                            <strong>
                              {prompt.mentionedAnswers}/
                              {prompt.successfulAnswers} · {prompt.mentionRate}%
                            </strong>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              </details>
            </div>
          ) : (
            <div className="benchmark-card benchmark-loading">
              Live results load from AeoKit’s public aggregate API. The
              denominator is successful tracked answers; failed runs are
              excluded.
            </div>
          )}
          <div className="benchmark-context">
            <article>
              <h3>What we asked</h3>
              {benchmark ? (
                <ul>
                  {benchmark.prompts.map((prompt) => (
                    <li key={prompt}>{prompt}</li>
                  ))}
                </ul>
              ) : (
                <p>
                  Common comparison and buying questions for CRM software. Exact
                  prompts load with the results.
                </p>
              )}
            </article>
            <article>
              <h3>Why the percentages differ</h3>
              <p>
                Each percentage is mentions divided by successful answers for
                that brand. A failed provider run is excluded, so sample sizes
                can differ. Brands named directly in a comparison prompt may
                also appear more often than alternatives.
              </p>
            </article>
            <article>
              <h3>Mention does not mean recommendation</h3>
              <p>
                A mention only means the brand appeared in the answer. It does
                not prove positive sentiment, recommendation rank, accuracy, or
                a citation. Those are separate AeoKit measurements.
              </p>
              {benchmark ? (
                <p className="benchmark-surfaces">
                  Surfaces:{" "}
                  {benchmark.surfaces.map(({ model }) => model).join(", ")}.
                </p>
              ) : null}
            </article>
          </div>
        </div>
      </section>
      <section className="section">
        <div className="container narrow seo-explainer">
          <p className="eyebrow">The practical difference</p>
          <h2>Rank the page, or supply the evidence.</h2>
          <div className="seo-comparison">
            <article>
              <h3>SEO</h3>
              <p>
                Builds page and domain signals to compete for a position in a
                ranked list of search results.
              </p>
            </article>
            <article>
              <h3>AEO</h3>
              <p>
                Makes a specific fact or source discoverable, extractable, and
                useful inside a generated answer.
              </p>
            </article>
          </div>
          <a className="button primary" href={agentUrl}>
            Audit with aeokit agent <ArrowRight aria-hidden="true" />
          </a>
        </div>
      </section>
    </>
  );
}
